import { useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  FileClock,
  FileUp,
  FileDown,
  GitCompareArrows,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { EmptyState, Field, StatusPill } from "../components/Ui";
import { blankDocumentRevision } from "../lib/projectStore";
import {
  exportBcfIssues,
  importBcfIssues,
} from "../lib/revisionIntelligence";
import type {
  ActionStatus,
  DesignRisk,
  DocumentRevision,
  DocumentStatus,
  ReviewStatus,
  RevisionComparison,
  SafetyIssuePin,
} from "../types/domain";

interface DocumentsProps {
  revisions: DocumentRevision[];
  comparisons: RevisionComparison[];
  risks: DesignRisk[];
  pins: SafetyIssuePin[];
  canUpload: boolean;
  onSave: (revision: DocumentRevision) => void;
  onRemove: (id: string) => void;
  onUpload?: (revision: DocumentRevision, file: File) => Promise<DocumentRevision>;
  onOpen?: (path: string) => Promise<void>;
  onSavePin: (pin: SafetyIssuePin) => void;
  onRemovePin: (id: string) => void;
  onSaveComparison: (comparison: RevisionComparison) => void;
  onImportPins: (pins: SafetyIssuePin[]) => void;
}

const statusTone = (status: DocumentStatus) =>
  status === "approved" ? "success" : status === "withdrawn" ? "danger" : "warning";

export function DocumentsView({
  revisions,
  comparisons,
  risks,
  pins,
  canUpload,
  onSave,
  onRemove,
  onUpload,
  onOpen,
  onSavePin,
  onRemovePin,
  onSaveComparison,
  onImportPins,
}: DocumentsProps) {
  const [draft, setDraft] = useState<DocumentRevision | null>(null);
  const [pinDraft, setPinDraft] = useState<SafetyIssuePin | null>(null);
  const [selectedRevisionId, setSelectedRevisionId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const bcfRef = useRef<HTMLInputElement>(null);
  const latest = useMemo(() => {
    const grouped = new Map<string, DocumentRevision[]>();
    for (const revision of revisions) {
      const key = revision.documentNumber || revision.id;
      grouped.set(key, [...(grouped.get(key) ?? []), revision]);
    }
    return [...grouped.entries()].map(([number, items]) => ({
      number,
      items: items.sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
      latest: items.sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0],
    }));
  }, [revisions]);
  const activeRevisionId = selectedRevisionId || revisions[0]?.id || "";
  const visiblePins = pins.filter((pin) => pin.documentRevisionId === activeRevisionId);
  const activeRevision = revisions.find((item) => item.id === activeRevisionId);

  const selectFile = async (file: File) => {
    if (!draft || !onUpload) return;
    setUploading(true);
    setUploadError("");
    try {
      const uploaded = await onUpload(draft, file);
      setDraft(uploaded);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <section className="module-intro">
        <div>
          <span className="feature-kicker"><FileClock size={16} /> Revision-aware safety information</span>
          <h2>Know which drawing carries each safety decision.</h2>
          <p>
            Register every issued revision, connect it to risks and duties, record what changed and
            prevent superseded information from silently remaining in the assurance trail.
          </p>
        </div>
        <a
          className="button button-secondary"
          href="https://www.buildingsmart.org/standards/bsi-standards/bim-collaboration-format/"
          target="_blank"
          rel="noreferrer"
        >
          BCF interoperability <ExternalLink size={14} />
        </a>
      </section>

      <section className="toolbar">
        <div className="revision-legend">
          <span><i className="dot-approved" /> approved</span>
          <span><i className="dot-shared" /> live review</span>
          <span><i className="dot-superseded" /> superseded</span>
        </div>
        <button className="button button-primary" onClick={() => setDraft(blankDocumentRevision())}>
          <Plus size={18} /> Register revision
        </button>
      </section>

      {revisions.length === 0 ? (
        <EmptyState
          icon={<GitCompareArrows size={32} />}
          title="No controlled revisions"
          action={
            <button className="button button-primary" onClick={() => setDraft(blankDocumentRevision())}>
              <Plus size={18} /> Add first revision
            </button>
          }
        >
          Add drawing, specification, calculation, plan and model revisions. Link every change that
          affects a risk so outdated controls can be found immediately.
        </EmptyState>
      ) : (
        <section className="revision-groups">
          {latest.map((group) => (
            <article className="revision-card" key={group.number}>
              <header>
                <div>
                  <span className="eyebrow">{group.latest.discipline || "Unassigned discipline"}</span>
                  <h3>{group.latest.documentNumber || "No number"} · {group.latest.title || "Untitled document"}</h3>
                </div>
                <StatusPill status={statusTone(group.latest.status)}>{group.latest.status}</StatusPill>
              </header>
              <div className="revision-timeline">
                {group.items.map((revision, index) => (
                  <div className={`revision-row revision-${revision.status}`} key={revision.id}>
                    <span className="revision-node">{revision.revision}</span>
                    <div>
                      <strong>{revision.changeSummary || "No change summary"}</strong>
                      <small>
                        {revision.issueDate || "No issue date"} · {revision.owner || "Unassigned"} ·
                        {" "}{revision.linkedRiskIds.length} safety link(s)
                      </small>
                    </div>
                    {revision.filePath && (
                      <button className="button-link" onClick={() => void onOpen?.(revision.filePath)}>
                        Open file <ExternalLink size={13} />
                      </button>
                    )}
                    <button className="button-link" onClick={() => setDraft(revision)}>Edit</button>
                    <button
                      className="icon-button danger"
                      aria-label="Delete draft revision"
                      disabled={Boolean(revision.filePath) || revision.status !== "draft"}
                      title={
                        revision.filePath || revision.status !== "draft"
                          ? "Issued or file-backed revisions are retained. Mark the record withdrawn or superseded."
                          : "Delete unissued draft revision"
                      }
                      onClick={() =>
                        window.confirm("Delete this unissued draft revision record?") &&
                        onRemove(revision.id)
                      }
                    >
                      <Trash2 size={15} />
                    </button>
                    {index < group.items.length - 1 && <span className="revision-line" />}
                  </div>
                ))}
              </div>
            </article>
          ))}
        </section>
      )}

      {comparisons.length > 0 && (
        <section className="comparison-register">
          <div className="section-heading-row">
            <div>
              <span className="eyebrow">Automatic change intelligence</span>
              <h2>Revision comparisons and reopened risks</h2>
            </div>
            <span className="comparison-engine">DSM-DIFF-1.0</span>
          </div>
          <div className="comparison-grid">
            {comparisons
              .slice()
              .sort((a, b) => b.comparedAt.localeCompare(a.comparedAt))
              .map((comparison) => {
                const current = revisions.find(
                  (item) => item.id === comparison.currentRevisionId,
                );
                const previous = revisions.find(
                  (item) => item.id === comparison.previousRevisionId,
                );
                return (
                  <article
                    className={`comparison-card comparison-${comparison.impactLevel}`}
                    key={comparison.id}
                  >
                    <header>
                      <div>
                        <span className="eyebrow">
                          {previous?.revision || "?"} → {current?.revision || "?"}
                        </span>
                        <h3>{current?.documentNumber || "Controlled revision"}</h3>
                      </div>
                      <strong>{comparison.impactLevel} impact</strong>
                    </header>
                    <div className="comparison-metrics">
                      <span><b>{comparison.changeScore}%</b> change score</span>
                      <span><b>{comparison.changedPages.length}</b> changed pages</span>
                      <span><b>{comparison.reopenedRiskIds.length}</b> risks reopened</span>
                    </div>
                    <p>
                      Safety terms:{" "}
                      {comparison.safetyKeywords.join(", ") || "none detected"}
                    </p>
                    <details>
                      <summary>Inspect detected changes</summary>
                      <div className="diff-columns">
                        <div>
                          <strong>Added</strong>
                          {comparison.addedText.slice(0, 8).map((text) => (
                            <p key={text}>+ {text}</p>
                          ))}
                        </div>
                        <div>
                          <strong>Removed</strong>
                          {comparison.removedText.slice(0, 8).map((text) => (
                            <p key={text}>− {text}</p>
                          ))}
                        </div>
                      </div>
                    </details>
                    <label className="field">
                      <span>Review disposition</span>
                      <select
                        value={comparison.reviewStatus}
                        onChange={(event) =>
                          onSaveComparison({
                            ...comparison,
                            reviewStatus: event.target
                              .value as RevisionComparison["reviewStatus"],
                          })
                        }
                      >
                        <option value="machine-screened">Machine screened</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="accepted">Accepted</option>
                      </select>
                    </label>
                  </article>
                );
              })}
          </div>
        </section>
      )}

      <section className="evidence-guidance success-guidance">
        <CheckCircle2 size={20} />
        <div>
          <strong>Electronic-record controls</strong>
          <p>
            Uploaded files are private, project-scoped and checksum-tracked. This supports the
            Regulation 3(2) requirements for retrieval, reproduction and protection from loss or
            unauthorised interference.
          </p>
        </div>
      </section>

      {revisions.length > 0 && (
        <section className="issue-map-section">
          <div className="section-heading-row">
            <div>
              <span className="eyebrow">Page-linked coordination</span>
              <h2>Safety issue map</h2>
            </div>
            <div className="issue-map-actions">
              <select value={activeRevisionId} onChange={(e) => setSelectedRevisionId(e.target.value)}>
                {revisions.map((revision) => (
                  <option key={revision.id} value={revision.id}>
                    {revision.documentNumber} · {revision.revision}
                  </option>
                ))}
              </select>
              <input
                ref={bcfRef}
                type="file"
                accept=".bcf,.bcfzip,.zip"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file || !activeRevisionId) return;
                  void importBcfIssues(file, activeRevisionId).then(onImportPins);
                  event.target.value = "";
                }}
              />
              <button
                className="button button-secondary"
                onClick={() => bcfRef.current?.click()}
              >
                <FileUp size={16} /> Import BCF
              </button>
              <button
                className="button button-secondary"
                disabled={!activeRevision || visiblePins.length === 0}
                onClick={() => {
                  if (!activeRevision) return;
                  void exportBcfIssues(activeRevision, visiblePins).then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const anchor = document.createElement("a");
                    anchor.href = url;
                    anchor.download = `${activeRevision.documentNumber || "design-safety"}-${activeRevision.revision}.bcfzip`;
                    anchor.click();
                    URL.revokeObjectURL(url);
                  });
                }}
              >
                <FileDown size={16} /> Export BCF
              </button>
              <button
                className="button button-secondary"
                onClick={() =>
                  setPinDraft({
                    id: crypto.randomUUID(),
                    documentRevisionId: activeRevisionId,
                    page: 1,
                    x: 50,
                    y: 50,
                    title: "",
                    description: "",
                    linkedRiskId: "",
                    owner: "",
                    dueDate: "",
                    status: "open",
                    bcfGuid: crypto.randomUUID(),
                    priority: "normal",
                    viewpointReference: "",
                    createdAt: new Date().toISOString(),
                  })
                }
              >
                <Plus size={17} /> Add page issue
              </button>
            </div>
          </div>
          <div className="issue-map-layout">
            <div className="sheet-map" aria-label="Safety issue coordinate map">
              <div className="sheet-title-block">Controlled drawing / document page</div>
              {visiblePins.map((pin, index) => (
                <button
                  key={pin.id}
                  className={`sheet-pin pin-${pin.status}`}
                  style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                  title={`${pin.title} · page ${pin.page}`}
                  onClick={() => setPinDraft(pin)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            <div className="issue-register">
              {visiblePins.length === 0 ? (
                <p>No page-linked safety issues for this revision.</p>
              ) : (
                visiblePins.map((pin, index) => (
                  <button key={pin.id} onClick={() => setPinDraft(pin)}>
                    <span>{index + 1}</span>
                    <div><strong>{pin.title || "Untitled issue"}</strong><small>Page {pin.page} · {pin.owner || "Unassigned"} · {pin.status}</small></div>
                  </button>
                ))
              )}
            </div>
          </div>
          {activeRevision?.ifcEntityCount !== null &&
            activeRevision?.ifcEntityCount !== undefined && (
              <div className="ifc-summary">
                <strong>IFC model screen</strong>
                <span>Schema {activeRevision.ifcSchema || "unknown"}</span>
                <span>{activeRevision.ifcEntityCount.toLocaleString()} entities</span>
                <span>{activeRevision.ifcSafetyEntities.length} safety-relevant lines</span>
              </div>
            )}
        </section>
      )}

      {draft && (
        <div className="modal-backdrop">
          <section className="modal-card" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div>
                <span className="eyebrow">Controlled document revision</span>
                <h2>{revisions.some((item) => item.id === draft.id) ? "Edit" : "Register"} revision</h2>
              </div>
              <button className="icon-button" onClick={() => setDraft(null)} aria-label="Close"><X size={21} /></button>
            </div>
            <div className="modal-scroll">
              <div className="form-grid">
                <Field label="Document number">
                  <input value={draft.documentNumber} onChange={(e) => setDraft({ ...draft, documentNumber: e.target.value })} />
                </Field>
                <Field label="Revision">
                  <input value={draft.revision} onChange={(e) => setDraft({ ...draft, revision: e.target.value })} />
                </Field>
                <Field label="Title" wide>
                  <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                </Field>
                <Field label="Type">
                  <select value={draft.documentType} onChange={(e) => setDraft({ ...draft, documentType: e.target.value as DocumentRevision["documentType"] })}>
                    <option value="drawing">Drawing</option>
                    <option value="specification">Specification</option>
                    <option value="calculation">Calculation</option>
                    <option value="model">Model</option>
                    <option value="plan">Plan</option>
                    <option value="report">Report</option>
                    <option value="record">Record</option>
                  </select>
                </Field>
                <Field label="Discipline">
                  <input value={draft.discipline} onChange={(e) => setDraft({ ...draft, discipline: e.target.value })} />
                </Field>
                <Field label="Issue status">
                  <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value as DocumentStatus })}>
                    <option value="draft">Draft</option>
                    <option value="shared">Shared for review</option>
                    <option value="approved">Approved</option>
                    <option value="superseded">Superseded</option>
                    <option value="withdrawn">Withdrawn</option>
                  </select>
                </Field>
                <Field label="Review status">
                  <select value={draft.reviewStatus} onChange={(e) => setDraft({ ...draft, reviewStatus: e.target.value as ReviewStatus })}>
                    <option value="draft">Draft</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="approved">Approved</option>
                  </select>
                </Field>
                <Field label="Issue date">
                  <input type="date" value={draft.issueDate} onChange={(e) => setDraft({ ...draft, issueDate: e.target.value })} />
                </Field>
                <Field label="Owner">
                  <input value={draft.owner} onChange={(e) => setDraft({ ...draft, owner: e.target.value })} />
                </Field>
                <Field label="Supersedes revision">
                  <select value={draft.supersedesId} onChange={(e) => setDraft({ ...draft, supersedesId: e.target.value })}>
                    <option value="">None</option>
                    {revisions.filter((item) => item.id !== draft.id).map((item) => (
                      <option key={item.id} value={item.id}>{item.documentNumber} · {item.revision}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Safety-relevant change summary" wide>
                  <textarea rows={3} value={draft.changeSummary} onChange={(e) => setDraft({ ...draft, changeSummary: e.target.value })} />
                </Field>
                <Field label="Linked design risks" wide>
                  <div className="check-grid">
                    {risks.map((risk) => (
                      <label key={risk.id}>
                        <input
                          type="checkbox"
                          checked={draft.linkedRiskIds.includes(risk.id)}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              linkedRiskIds: e.target.checked
                                ? [...draft.linkedRiskIds, risk.id]
                                : draft.linkedRiskIds.filter((id) => id !== risk.id),
                            })
                          }
                        />
                        <span>{risk.designElement || risk.hazard || "Untitled risk"}</span>
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label="Secure project file" wide>
                  <div className="upload-zone">
                    <FileUp size={24} />
                    <div>
                      <strong>{draft.fileName || "No file uploaded"}</strong>
                      <small>
                        {canUpload
                          ? "PDF, image, Office document, IFC or BCF · maximum 50 MB"
                          : "Secure upload is available in the signed-in cloud workspace."}
                      </small>
                    </div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.ifc,.bcf,.txt,.csv"
                      hidden
                      disabled={!canUpload || uploading}
                      onChange={(e) => e.target.files?.[0] && void selectFile(e.target.files[0])}
                    />
                    <button className="button button-secondary" disabled={!canUpload || uploading} onClick={() => fileRef.current?.click()}>
                      {uploading ? "Uploading…" : "Choose file"}
                    </button>
                  </div>
                  {draft.checksum && <small className="checksum">SHA-256 · {draft.checksum}</small>}
                  {uploadError && <p className="inline-error"><AlertTriangle size={15} /> {uploadError}</p>}
                </Field>
              </div>
            </div>
            <div className="modal-actions">
              <div className="quality-summary">
                {draft.documentNumber && draft.title && draft.changeSummary ? (
                  <><CheckCircle2 size={17} /> Revision impact described</>
                ) : (
                  <><AlertTriangle size={17} /> Number, title and change summary are required</>
                )}
              </div>
              <button className="button button-secondary" onClick={() => setDraft(null)}>Cancel</button>
              <button
                className="button button-primary"
                disabled={!draft.documentNumber.trim() || !draft.title.trim() || !draft.changeSummary.trim()}
                onClick={() => {
                  onSave({ ...draft, updatedAt: new Date().toISOString() });
                  setDraft(null);
                }}
              >
                Save revision
              </button>
            </div>
          </section>
        </div>
      )}

      {pinDraft && (
        <div className="modal-backdrop">
          <section className="modal-card modal-compact" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div><span className="eyebrow">BCF-ready safety issue</span><h2>Page-linked coordination issue</h2></div>
              <button className="icon-button" onClick={() => setPinDraft(null)} aria-label="Close"><X size={21} /></button>
            </div>
            <div className="modal-scroll">
              <div className="form-grid">
                <Field label="Issue title" wide>
                  <input value={pinDraft.title} onChange={(e) => setPinDraft({ ...pinDraft, title: e.target.value })} />
                </Field>
                <Field label="Page">
                  <input type="number" min={1} value={pinDraft.page} onChange={(e) => setPinDraft({ ...pinDraft, page: Number(e.target.value) })} />
                </Field>
                <Field label="Status">
                  <select value={pinDraft.status} onChange={(e) => setPinDraft({ ...pinDraft, status: e.target.value as ActionStatus })}>
                    <option value="open">Open</option>
                    <option value="in-progress">In progress</option>
                    <option value="blocked">Blocked</option>
                    <option value="complete">Complete</option>
                  </select>
                </Field>
                <Field label="Priority">
                  <select
                    value={pinDraft.priority}
                    onChange={(e) =>
                      setPinDraft({
                        ...pinDraft,
                        priority: e.target.value as SafetyIssuePin["priority"],
                      })
                    }
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </Field>
                <Field label="BCF GUID">
                  <input
                    value={pinDraft.bcfGuid}
                    onChange={(e) => setPinDraft({ ...pinDraft, bcfGuid: e.target.value })}
                  />
                </Field>
                <Field label="Horizontal position (%)">
                  <input type="number" min={2} max={98} value={pinDraft.x} onChange={(e) => setPinDraft({ ...pinDraft, x: Number(e.target.value) })} />
                </Field>
                <Field label="Vertical position (%)">
                  <input type="number" min={2} max={98} value={pinDraft.y} onChange={(e) => setPinDraft({ ...pinDraft, y: Number(e.target.value) })} />
                </Field>
                <Field label="Description" wide>
                  <textarea rows={4} value={pinDraft.description} onChange={(e) => setPinDraft({ ...pinDraft, description: e.target.value })} />
                </Field>
                <Field label="Owner">
                  <input value={pinDraft.owner} onChange={(e) => setPinDraft({ ...pinDraft, owner: e.target.value })} />
                </Field>
                <Field label="Due date">
                  <input type="date" value={pinDraft.dueDate} onChange={(e) => setPinDraft({ ...pinDraft, dueDate: e.target.value })} />
                </Field>
                <Field label="Linked design risk" wide>
                  <select value={pinDraft.linkedRiskId} onChange={(e) => setPinDraft({ ...pinDraft, linkedRiskId: e.target.value })}>
                    <option value="">No linked risk</option>
                    {risks.map((risk) => <option key={risk.id} value={risk.id}>{risk.designElement || risk.hazard || "Untitled risk"}</option>)}
                  </select>
                </Field>
              </div>
            </div>
            <div className="modal-actions">
              {pins.some((pin) => pin.id === pinDraft.id) && (
                <button
                  className="button button-danger"
                  onClick={() => {
                    onRemovePin(pinDraft.id);
                    setPinDraft(null);
                  }}
                >
                  Delete issue
                </button>
              )}
              <button className="button button-secondary" onClick={() => setPinDraft(null)}>Cancel</button>
              <button
                className="button button-primary"
                disabled={!pinDraft.title.trim() || !pinDraft.owner.trim()}
                onClick={() => {
                  onSavePin(pinDraft);
                  setPinDraft(null);
                }}
              >
                Save page issue
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
